import { useState } from "react";
import api from "../../lib/api";
import { DISCOVERY_SECTIONS } from "../../data/questions";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loader2, Pencil, Check, X, RefreshCw, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

const LABEL_MAP = {
  sales_process: "Sales Process", marketing_process: "Marketing", customer_experience: "Customer Experience",
  operations_admin: "Operations & Admin", team_structure: "Team Structure", software_stack: "Software Stack",
  automation_usage: "Automation Usage", growth_constraints: "Growth Constraints", pain_points: "Pain Points",
  ai_readiness: "AI Readiness", revenue_model: "Revenue Model", company_overview: "Company Overview",
};

function QuestionField({ question, value, onChange }) {
  const val = value !== undefined && value !== null ? String(value) : "";

  if (question.type === "radio" && question.options) {
    return (
      <div className="space-y-1.5">
        {question.options.map(opt => (
          <label key={opt.value} className={`flex items-center gap-2.5 p-2.5 border cursor-pointer transition-colors ${val === opt.value ? "border-[#0033CC] bg-[#0033CC]/[0.03]" : "border-slate-200 hover:border-slate-300"}`}>
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${val === opt.value ? "border-[#0033CC]" : "border-slate-300"}`}>
              {val === opt.value && <div className="w-2 h-2 rounded-full bg-[#0033CC]" />}
            </div>
            <span className="text-xs font-['IBM_Plex_Sans'] text-[#0F172A]">{opt.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (question.type === "scale" && question.labels) {
    const numVal = parseInt(val) || 0;
    return (
      <div className="space-y-2">
        <div className="flex gap-1">
          {question.labels.map((label, i) => {
            const scaleVal = String(i + 1);
            return (
              <button key={i} onClick={() => onChange(scaleVal)}
                className={`flex-1 py-2.5 px-1 text-center border transition-colors ${val === scaleVal ? "bg-[#0033CC] text-white border-[#0033CC]" : "border-slate-200 hover:border-[#0033CC] text-slate-600"}`}>
                <div className="text-xs font-['JetBrains_Mono'] font-bold mb-0.5">{i + 1}</div>
                <div className="text-[9px] font-['IBM_Plex_Sans'] leading-tight">{label}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Fallback: text input
  return <Input value={val} onChange={e => onChange(e.target.value)} className="rounded-none border-slate-300 h-9 text-sm" />;
}

function CompanyInfoEditor({ data, onChange }) {
  const fields = [
    { key: "company_name", label: "Company Name" },
    { key: "industry", label: "Industry" },
    { key: "sub_industry", label: "Sub-Industry" },
    { key: "company_size", label: "Team Size" },
    { key: "annual_revenue", label: "Annual Revenue" },
    { key: "website", label: "Website" },
    { key: "business_description", label: "Business Description" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3">
      {fields.map(f => (
        <div key={f.key} className={f.key === "business_description" ? "col-span-2" : ""}>
          <Label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">{f.label}</Label>
          <Input value={data[f.key] || ""} onChange={e => onChange({ ...data, [f.key]: e.target.value })} className="rounded-none border-slate-300 h-9 text-sm" />
        </div>
      ))}
    </div>
  );
}

export default function BlueprintAnswersEditor({ auditId, auditData, onUpdate }) {
  const [editingSection, setEditingSection] = useState(null);
  const [editAnswers, setEditAnswers] = useState({});
  const [editCompanyInfo, setEditCompanyInfo] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const answers = auditData?.answers || {};
  const companyInfo = auditData?.company_info || {};

  const startEdit = (key) => {
    if (key === "company_info") {
      setEditCompanyInfo({ ...companyInfo });
    } else {
      setEditAnswers({ ...(answers[key] || {}) });
    }
    setEditingSection(key);
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditAnswers({});
    setEditCompanyInfo({});
  };

  const saveSection = async () => {
    if (!editingSection) return;
    setSaving(true);
    try {
      if (editingSection === "company_info") {
        await api.post(`/audit/admin/edit/${auditId}`, { company_info: editCompanyInfo });
      } else {
        await api.post("/audit/admin/save", { audit_id: auditId, section: editingSection, answers: editAnswers });
      }
      cancelEdit();
      onUpdate?.();
    } catch (e) { alert("Save failed: " + e.message); }
    finally { setSaving(false); }
  };

  const resetAndReenter = async () => {
    if (!window.confirm("Reset this blueprint to in-progress? This will clear the report and scores so you can re-enter the discovery flow. The answers will be preserved.")) return;
    setResetting(true);
    try {
      await api.post(`/audit/admin/edit/${auditId}`, { reset_to_in_progress: true });
      onUpdate?.();
    } catch (e) { alert("Reset failed: " + e.message); }
    finally { setResetting(false); }
  };

  const getOptionLabel = (question, val) => {
    if (question.type === "radio" && question.options) {
      const opt = question.options.find(o => o.value === String(val));
      return opt ? opt.label : String(val);
    }
    if (question.type === "scale" && question.labels) {
      const idx = parseInt(val) - 1;
      return question.labels[idx] || String(val);
    }
    return String(val);
  };

  const allSections = [
    { key: "company_info", label: "Company Info", data: companyInfo, questions: null },
    ...DISCOVERY_SECTIONS.map(s => ({ key: s.id, label: LABEL_MAP[s.id] || s.title, data: answers[s.id] || {}, questions: s.questions, description: s.description })),
  ];

  return (
    <div className="space-y-2" data-testid="blueprint-answers-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Discovery Answers</h3>
          <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">View and edit responses. Click a section to expand.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => window.open(`/blueprint?audit_id=${auditId}`, "_blank")} variant="outline" size="sm" className="rounded-none h-8 text-xs font-['Outfit'] border-[#0033CC] text-[#0033CC]" data-testid="open-wizard-btn">
            <ExternalLink className="w-3 h-3 mr-1.5" />Open in Wizard
          </Button>
          <Button onClick={resetAndReenter} disabled={resetting} variant="outline" size="sm" className="rounded-none h-8 text-xs font-['Outfit'] border-amber-500 text-amber-600" data-testid="reset-blueprint-btn">
            {resetting ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <RefreshCw className="w-3 h-3 mr-1.5" />}Reset to In-Progress
          </Button>
        </div>
      </div>

      {allSections.map(({ key, label, data, questions, description }) => {
        const isEditing = editingSection === key;
        const isExpanded = expanded === key || isEditing;
        const hasData = data && Object.keys(data).length > 0;

        return (
          <div key={key} className={`bg-white border ${isEditing ? "border-[#0033CC]" : "border-slate-200"}`} data-testid={`answer-section-${key}`}>
            {/* Header */}
            <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => !isEditing && setExpanded(isExpanded ? null : key)}>
              <div className="flex items-center gap-2">
                {!isEditing && (isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />)}
                <span className="text-sm font-['Outfit'] font-medium text-[#0F172A]">{label}</span>
                {hasData ? (
                  <span className="text-[10px] font-['JetBrains_Mono'] bg-emerald-50 text-emerald-700 px-1.5 py-0.5">{Object.keys(data).length} answers</span>
                ) : (
                  <span className="text-[10px] font-['JetBrains_Mono'] bg-slate-100 text-slate-400 px-1.5 py-0.5">empty</span>
                )}
              </div>
              <button onClick={(e) => { e.stopPropagation(); isEditing ? cancelEdit() : startEdit(key); }} className="text-slate-400 hover:text-[#0033CC] p-1" data-testid={`edit-answer-${key}`}>
                {isEditing ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
              </button>
            </div>

            {/* View Mode */}
            {isExpanded && !isEditing && hasData && (
              <div className="px-4 pb-4 space-y-2">
                {key === "company_info" ? (
                  Object.entries(data).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between items-start py-1.5 border-b border-slate-50">
                      <span className="text-xs text-slate-500 capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="text-xs text-[#0F172A] font-['IBM_Plex_Sans'] text-right max-w-[60%]">{String(v)}</span>
                    </div>
                  ))
                ) : (
                  (questions || []).map(q => {
                    const val = data[q.id];
                    if (val === undefined || val === null) return null;
                    return (
                      <div key={q.id} className="py-2 border-b border-slate-50">
                        <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mb-1">{q.text}</p>
                        <p className="text-xs font-['Outfit'] font-medium text-[#0F172A]">{getOptionLabel(q, val)}</p>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Edit Mode */}
            {isEditing && (
              <div className="px-4 pb-4">
                {description && <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-4">{description}</p>}

                {key === "company_info" ? (
                  <CompanyInfoEditor data={editCompanyInfo} onChange={setEditCompanyInfo} />
                ) : (
                  <div className="space-y-5">
                    {(questions || []).map(q => (
                      <div key={q.id}>
                        <p className="text-sm text-[#0F172A] font-['IBM_Plex_Sans'] mb-2">{q.text}</p>
                        <QuestionField question={q} value={editAnswers[q.id]} onChange={v => setEditAnswers(prev => ({ ...prev, [q.id]: v }))} />
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-slate-100">
                  <Button onClick={cancelEdit} variant="outline" size="sm" className="rounded-none text-xs font-['Outfit']"><X className="w-3 h-3 mr-1" />Cancel</Button>
                  <Button onClick={saveSection} disabled={saving} size="sm" className="rounded-none bg-[#0033CC] text-white text-xs font-['Outfit']" data-testid={`save-answer-${key}`}>
                    {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}Save Section
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
